import { useState, useEffect } from 'react'
import { TenantGuard } from '@/components/TenantGuard'
import { useAuth } from '@/hooks/useApi'
import { useTenantContext } from '@/contexts/TenantContext'
import { cognitoAuth } from '@/lib/cognito'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DataTable } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Copy, Check, Eye, EyeOff, User, Plus, Trash2, UserX, Key, UserCog, MoreHorizontal, UserCheck } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'

export function Users() {
  const { isSuperAdmin, isTenantManager, user, isImpersonating, impersonationData, refreshUser } = useAuth();
  const { selectedTenantId, isResolvingSubdomain } = useTenantContext();
  
  // When impersonating, use the impersonated user's tenant context
  // For subdomains, always use selectedTenantId (resolved from subdomain)
  // For super admins on admin.queuemein.com, use selectedTenantId from dropdown
  // For regular users, use their tenant_from_group
  const activeTenantId = isImpersonating ? 
    impersonationData?.impersonatedUser?.tenant_id :
    (selectedTenantId || user?.tenant_from_group);
  const [copied, setCopied] = useState(false)
  const [showToken, setShowToken] = useState(false)
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserRole, setNewUserRole] = useState('manager')
  const [newUserFirstName, setNewUserFirstName] = useState('')
  const [newUserLastName, setNewUserLastName] = useState('')
  const [sendEmail, setSendEmail] = useState(false)
  const [addingUser, setAddingUser] = useState(false)
  const [error, setError] = useState('')
  const [createdUserPassword, setCreatedUserPassword] = useState('')
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordCopied, setPasswordCopied] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [newRole, setNewRole] = useState('')
  const [changingRole, setChangingRole] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editFirstName, setEditFirstName] = useState('')
  const [editLastName, setEditLastName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editTimezone, setEditTimezone] = useState('')
  const [updatingUser, setUpdatingUser] = useState(false)
  const [myTimezone, setMyTimezone] = useState('')

  const [fetchError, setFetchError] = useState<string | null>(null)
  const [showMyProfileModal, setShowMyProfileModal] = useState(false)
  const [myFirstName, setMyFirstName] = useState('')
  const [myLastName, setMyLastName] = useState('')
  const [myEmail, setMyEmail] = useState('')
  const [myProfileTimezone, setMyProfileTimezone] = useState('')
  const [updatingMyProfile, setUpdatingMyProfile] = useState(false)
  const [emailChanged, setEmailChanged] = useState(false)
  
  const token = cognitoAuth.getIdToken()
  const maskedToken = token ? `${token.substring(0, 20)}...${token.substring(token.length - 20)}` : 'No token available'
  
  const fetchUsers = async () => {
    console.log('fetchUsers called:', { activeTenantId, isResolvingSubdomain, selectedTenantId })
    if (!activeTenantId || isResolvingSubdomain) {
      console.log('fetchUsers early return:', { activeTenantId, isResolvingSubdomain })
      return
    }
    
    try {
      setLoading(true)
      setFetchError(null)
      console.log('Making getUsers API call with tenantId:', activeTenantId)
      const response = await apiClient.getUsers(activeTenantId)
      console.log('getUsers response:', response)
      setUsers(response.users || [])
      
      // Also get current user's timezone
      const meResponse = await apiClient.getMe()
      setMyTimezone(meResponse.timezone || 'UTC')
    } catch (err: any) {
      console.error('Failed to fetch users:', err)
      setFetchError(`Failed to load users: ${err.message || 'Server error'}`)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }
  
  useEffect(() => {
    fetchUsers()
  }, [activeTenantId, isResolvingSubdomain])
  
  // Listen for tenant changes and refresh data
  useEffect(() => {
    const handleTenantChange = () => {
      fetchUsers()
    }
    
    window.addEventListener('tenant-changed', handleTenantChange)
    
    return () => {
      window.removeEventListener('tenant-changed', handleTenantChange)
    }
  }, [])
  
  const copyToken = async () => {
    if (token) {
      try {
        await navigator.clipboard.writeText(token)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy token:', err)
      }
    }
  }
  
  const copyPassword = async () => {
    if (createdUserPassword) {
      try {
        await navigator.clipboard.writeText(createdUserPassword)
        setPasswordCopied(true)
        setTimeout(() => setPasswordCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy password:', err)
      }
    }
  }
  
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newUserEmail.trim()) {
      setError('Email is required')
      return
    }
    
    if (!newUserFirstName.trim()) {
      setError('First name is required')
      return
    }
    
    if (!newUserLastName.trim()) {
      setError('Last name is required')
      return
    }
    
    if (!activeTenantId) return
    
    setAddingUser(true)
    setError('')
    
    try {
      const response = await apiClient.createUser(activeTenantId, {
        email: newUserEmail.trim(),
        role: newUserRole,
        first_name: newUserFirstName.trim(),
        last_name: newUserLastName.trim(),
        send_email: sendEmail
      })
      
      // Reset form
      setNewUserEmail('')
      setNewUserRole('manager')
      setNewUserFirstName('')
      setNewUserLastName('')
      setSendEmail(false)
      setShowAddModal(false)
      
      // Show password if email was not sent
      if (!sendEmail && (response as any).user?.temp_password) {
        setCreatedUserPassword((response as any).user.temp_password)
        setShowPasswordModal(true)
      }
      
      fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user')
    } finally {
      setAddingUser(false)
    }
  }
  
  const handleDisableUser = async (username: string) => {
    if (!activeTenantId) return
    
    try {
      await apiClient.disableUser(activeTenantId, username)
      fetchUsers()
    } catch (err) {
      alert('Failed to disable user')
    }
  }
  
  const handleEnableUser = async (username: string) => {
    if (!activeTenantId) return
    
    try {
      await apiClient.enableUser(activeTenantId, username)
      fetchUsers()
    } catch (err) {
      alert('Failed to enable user')
    }
  }
  
  const handleDeleteUser = async (username: string, email: string) => {
    if (!confirm(`Delete user ${email}? This cannot be undone.`)) return
    if (!activeTenantId) return
    
    try {
      await apiClient.deleteUser(activeTenantId, username)
      fetchUsers()
    } catch (err) {
      alert('Failed to delete user')
    }
  }
  
  const handleResetPassword = async (username: string, email: string) => {
    if (!confirm(`Send password reset email to ${email}?`)) return
    if (!activeTenantId) return
    
    try {
      await apiClient.resetUserPassword(activeTenantId, username)
      alert('Password reset email sent successfully')
    } catch (err) {
      alert('Failed to send password reset email')
    }
  }
  
  const handleChangeRole = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUser || !activeTenantId) return
    
    setChangingRole(true)
    setError('')
    
    try {
      await apiClient.changeUserRole(activeTenantId, selectedUser.username, newRole)
      setShowRoleModal(false)
      setSelectedUser(null)
      fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change role')
    } finally {
      setChangingRole(false)
    }
  }
  
  const handleImpersonateUser = (userToImpersonate: any) => {
    if (!confirm(`Impersonate ${userToImpersonate.first_name} ${userToImpersonate.last_name} (${userToImpersonate.email})?`)) return
    
    // Store impersonation data in localStorage
    const impersonationData = {
      originalUser: {
        email: user?.email,
        name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email,
        role: user?.role,
        tenant_id: user?.tenant_from_group
      },
      impersonatedUser: {
        email: userToImpersonate.email,
        name: `${userToImpersonate.first_name} ${userToImpersonate.last_name}`,
        role: userToImpersonate.role,
        username: userToImpersonate.username,
        tenant_id: activeTenantId // Use the current tenant context
      },
      startTime: new Date().toISOString()
    }
    
    localStorage.setItem('impersonation', JSON.stringify(impersonationData))
    
    // Trigger a custom event to notify other components
    window.dispatchEvent(new CustomEvent('impersonation-started', { detail: impersonationData }))
    
    // Refresh the page to apply impersonation
    window.location.reload()
  }
  
  const openRoleModal = (user: any) => {
    setSelectedUser(user)
    setNewRole(user.role)
    setShowRoleModal(true)
    setError('')
  }
  
  const openEditModal = (user: any) => {
    setSelectedUser(user)
    setEditFirstName(user.first_name || '')
    setEditLastName(user.last_name || '')
    setEditEmail(user.email)
    setEditTimezone(user.timezone || 'UTC')
    setShowEditModal(true)
    setError('')
  }
  
  const openMyProfileModal = () => {
    setMyFirstName(user?.first_name || '')
    setMyLastName(user?.last_name || '')
    setMyEmail(user?.email || '')
    setMyProfileTimezone(myTimezone || 'UTC')
    setEmailChanged(false)
    setShowMyProfileModal(true)
    setError('')
  }

  const handleUpdateMyProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!myFirstName.trim()) {
      setError('First name is required')
      return
    }
    
    if (!myLastName.trim()) {
      setError('Last name is required')
      return
    }
    
    if (!myEmail.trim()) {
      setError('Email is required')
      return
    }
    
    // Email change is handled by Cognito verification
    
    setUpdatingMyProfile(true)
    setError('')
    
    try {
      // Update via API client
      await apiClient.updateMyProfile({
        first_name: myFirstName.trim(),
        last_name: myLastName.trim(),
        email: myEmail.trim(),
        timezone: myProfileTimezone
      })
      
      // Update local timezone state immediately
      setMyTimezone(myProfileTimezone)
      
      // Close modal
      setShowMyProfileModal(false)
      
      // Refresh user data in auth context and local data
      await refreshUser()
      await fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setUpdatingMyProfile(false)
    }
  }

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUser || !activeTenantId) return
    
    if (!editFirstName.trim()) {
      setError('First name is required')
      return
    }
    
    if (!editLastName.trim()) {
      setError('Last name is required')
      return
    }
    
    if (!editEmail.trim()) {
      setError('Email is required')
      return
    }
    
    setUpdatingUser(true)
    setError('')
    
    try {
      await apiClient.updateUser(activeTenantId, selectedUser.username, {
        first_name: editFirstName.trim(),
        last_name: editLastName.trim(),
        email: editEmail.trim(),
        timezone: editTimezone
      })
      setShowEditModal(false)
      setSelectedUser(null)
      fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setUpdatingUser(false)
    }
  }
  
  const userColumns = [
    { 
      key: 'email' as const, 
      header: 'Name & Email',
      render: (email: string, row: any) => (
        <div>
          <div className="font-medium">{row.first_name} {row.last_name}</div>
          <div className="text-sm text-gray-500">{email}</div>
          <div className="text-xs text-gray-400">{row.timezone || 'UTC'}</div>
        </div>
      )
    },
    { 
      key: 'role' as const, 
      header: 'Role',
      render: (value: string) => (
        <Badge variant={value === 'admin' ? 'default' : 'secondary'}>
          {value}
        </Badge>
      )
    },
    { 
      key: 'status' as const, 
      header: 'Status',
      render: (value: string, row: any) => (
        <div className="space-y-1">
          <Badge variant={value === 'CONFIRMED' ? 'default' : 'secondary'}>
            {value}
          </Badge>
          <div>
            <Badge variant={row.enabled ? 'default' : 'destructive'} className="text-xs">
              {row.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </div>
      )
    },
    {
      key: 'username' as const,
      header: 'Actions',
      render: (username: string, row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => openEditModal(row)} disabled={isTenantManager}>
              <UserCog className="h-4 w-4 mr-2" />
              Edit User
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleResetPassword(username, row.email)} disabled={isTenantManager}>
              <Key className="h-4 w-4 mr-2" />
              Reset Password
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openRoleModal(row)} disabled={isTenantManager}>
              <UserCog className="h-4 w-4 mr-2" />
              Change Role
            </DropdownMenuItem>
            {row.enabled ? (
              <DropdownMenuItem onClick={() => handleDisableUser(username)} disabled={isTenantManager}>
                <UserX className="h-4 w-4 mr-2" />
                Disable User
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => handleEnableUser(username)} disabled={isTenantManager}>
                <User className="h-4 w-4 mr-2" />
                Enable User
              </DropdownMenuItem>
            )}
            {(isSuperAdmin || user?.role === 'admin') && (
              <DropdownMenuItem onClick={() => handleImpersonateUser(row)} disabled={isTenantManager}>
                <UserCheck className="h-4 w-4 mr-2" />
                Impersonate User
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              onClick={() => handleDeleteUser(username, row.email)}
              className="text-red-600"
              disabled={isTenantManager}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete User
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ]
  
  const usersContent = (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">User Management</h1>
        <p className="text-gray-600">
          Manage users
          {isImpersonating && (
            <span className="ml-2 text-orange-600 font-medium">
              (Impersonating: {impersonationData?.impersonatedUser?.name})
            </span>
          )}
        </p>
      </div>
      
      {/* Personal Settings */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                My Account
              </CardTitle>
              <CardDescription>Your personal account settings and API access</CardDescription>
            </div>
            <Button variant="outline" onClick={openMyProfileModal}>
              <UserCog className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Name</label>
              <Input value={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Not set'} disabled className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Email</label>
              <Input value={user?.email || ''} disabled className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Role</label>
              <div className="mt-1">
                <Badge variant={user?.role === 'super_admin' ? 'default' : user?.role === 'tenant_admin' ? 'secondary' : 'outline'}>
                  {user?.role === 'super_admin' ? 'SUPER ADMIN' : user?.role === 'tenant_admin' ? 'TENANT ADMIN' : user?.role === 'tenant_manager' ? 'MANAGER' : 'VIEWER'}
                </Badge>
              </div>
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Timezone</label>
            <div className="mt-1">
              <Input value={myTimezone || 'UTC'} disabled className="bg-gray-50" />
              <p className="text-xs text-gray-500 mt-1">
                Update your timezone in the Edit Profile section above.
              </p>
            </div>
          </div>
          
          {!isTenantManager && (
            <div>
              <label className="text-sm font-medium text-gray-700">API Bearer Token</label>
            <div className="flex gap-2 mt-1">
              <Input 
                value={showToken ? token || '' : maskedToken} 
                disabled 
                className="font-mono text-xs"
                type={showToken ? 'text' : 'password'}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowToken(!showToken)}
                className="px-3"
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={copyToken}
                disabled={!token}
                className="px-3"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            {copied && (
              <p className="text-sm text-green-600 mt-1">Token copied to clipboard!</p>
            )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Tenant Users */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Tenant Users</CardTitle>
              <CardDescription>Manage users for this tenant</CardDescription>
            </div>
            {!isTenantManager && (
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading || isResolvingSubdomain ? (
            <div>{isResolvingSubdomain ? 'Resolving tenant...' : 'Loading users...'}</div>
          ) : fetchError ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-2">{fetchError}</div>
              <Button variant="outline" onClick={fetchUsers}>
                Try Again
              </Button>
            </div>
          ) : (
            <DataTable 
              data={users} 
              columns={userColumns}
              searchable
              searchPlaceholder="Search users..."
            />
          )}
        </CardContent>
      </Card>
      
      {/* Add User Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user for this tenant. They will receive an email invitation.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={newUserFirstName}
                  onChange={(e) => setNewUserFirstName(e.target.value)}
                  placeholder="John"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={newUserLastName}
                  onChange={(e) => setNewUserLastName(e.target.value)}
                  placeholder="Doe"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@example.com"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={newUserRole} onValueChange={setNewUserRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="sendEmail" 
                checked={sendEmail} 
                onCheckedChange={(checked) => setSendEmail(checked === true)}
              />
              <Label htmlFor="sendEmail" className="text-sm font-normal">
                Send welcome email with temporary password
              </Label>
            </div>
            
            {!sendEmail && (
              <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ No email will be sent. You'll need to provide the temporary password to the user manually.
              </div>
            )}
            
            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addingUser}>
                {addingUser ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="editFirstName">First Name *</Label>
                <Input
                  id="editFirstName"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="editLastName">Last Name *</Label>
                <Input
                  id="editLastName"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="editEmail">Email *</Label>
              <Input
                id="editEmail"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="editTimezone">Timezone</Label>
              <Select value={editTimezone} onValueChange={setEditTimezone}>
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">UTC</SelectItem>
                  <SelectItem value="America/New_York">Eastern Time</SelectItem>
                  <SelectItem value="America/Chicago">Central Time</SelectItem>
                  <SelectItem value="America/Denver">Mountain Time</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                  <SelectItem value="America/Phoenix">Arizona Time</SelectItem>
                  <SelectItem value="Europe/London">London Time</SelectItem>
                  <SelectItem value="Europe/Paris">Paris Time</SelectItem>
                  <SelectItem value="Asia/Tokyo">Tokyo Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updatingUser}>
                {updatingUser ? 'Updating...' : 'Update User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Change Role Modal */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Change the role for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleChangeRole} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newRole">New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRoleModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={changingRole}>
                {changingRole ? 'Changing...' : 'Change Role'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* My Profile Edit Modal */}
      <Dialog open={showMyProfileModal} onOpenChange={setShowMyProfileModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit My Profile</DialogTitle>
            <DialogDescription>
              Update your personal information. Email changes require verification.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateMyProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="myFirstName">First Name *</Label>
                <Input
                  id="myFirstName"
                  value={myFirstName}
                  onChange={(e) => setMyFirstName(e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="myLastName">Last Name *</Label>
                <Input
                  id="myLastName"
                  value={myLastName}
                  onChange={(e) => setMyLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="myEmail">Email *</Label>
              <Input
                id="myEmail"
                type="email"
                value={myEmail}
                onChange={(e) => {
                  setMyEmail(e.target.value)
                  setEmailChanged(e.target.value.trim() !== (user?.email || ''))
                }}
                required
              />
              <p className="text-xs text-gray-500">
                Changing your email will require verification and may affect your login.
              </p>
            </div>
            
            {emailChanged && (
              <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ Changing your email will require verification and may affect your login. You'll receive a verification email at the new address.
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="myProfileTimezone">Timezone</Label>
              <Select value={myProfileTimezone} onValueChange={setMyProfileTimezone}>
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">UTC</SelectItem>
                  <SelectItem value="America/New_York">Eastern Time</SelectItem>
                  <SelectItem value="America/Chicago">Central Time</SelectItem>
                  <SelectItem value="America/Denver">Mountain Time</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                  <SelectItem value="America/Phoenix">Arizona Time</SelectItem>
                  <SelectItem value="Europe/London">London Time</SelectItem>
                  <SelectItem value="Europe/Paris">Paris Time</SelectItem>
                  <SelectItem value="Asia/Tokyo">Tokyo Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMyProfileModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updatingMyProfile}>
                {updatingMyProfile ? 'Updating...' : 'Update Profile'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Password Display Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>User Created Successfully</DialogTitle>
            <DialogDescription>
              The user has been created. Please provide them with this temporary password.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <Label className="text-sm font-medium text-gray-700">Temporary Password</Label>
              <div className="flex gap-2 mt-2">
                <Input 
                  value={createdUserPassword} 
                  readOnly 
                  className="font-mono text-sm"
                  type="text"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyPassword}
                  className="px-3"
                >
                  {passwordCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              {passwordCopied && (
                <p className="text-sm text-green-600 mt-1">Password copied to clipboard!</p>
              )}
            </div>
            
            <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded">
              ⚠️ <strong>Important:</strong> This password will not be shown again. Make sure to copy it and provide it to the user securely.
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => {
              setShowPasswordModal(false)
              setCreatedUserPassword('')
              setPasswordCopied(false)
            }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
  
  return (
    <TenantGuard>
      {usersContent}
    </TenantGuard>
  )
}